import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '../../../../../shared/source/context/context';
import { globalSearchJobResponseSchema } from '../../../data-contract/global-search';

import type { GeneralFilter } from '../../../../../shared/source/data-contract/filter';
import type { GlobalSearchJobResponse } from '../../../data-contract/global-search';
import type { SortOption } from '../../pages/talent-directory/sort-option';
import type { UseQueryResult } from '@tanstack/react-query';
import type Maybe from 'true-myth/maybe';

export const useGlobalSearchDirectoryJobs = ({
    filterPayload,
    page,
    pageSize,
    searchTerm,
    sortOptions,
}: {
    readonly filterPayload: Maybe<GeneralFilter>;
    readonly page: number;
    readonly pageSize: number;
    readonly searchTerm: Maybe<string>;
    readonly sortOptions: readonly SortOption[];
}): UseQueryResult<GlobalSearchJobResponse, Error> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const hasSearchTerm = searchTerm.isJust;
    const pathname = hasSearchTerm ? '/global/search/jobs' : '/global/directory/jobs/filter';
    const operation = searchTerm.isJust ? 'get' : 'postJson';

    return useQuery({
        queryFn: async () => {
            const result = searchTerm.isJust
                ? await validatingCobrainerApiFetcher.get(globalSearchJobResponseSchema, {
                      pathname,
                      requestOptions: {
                          searchParams: {
                              page: String(page),
                              searchTerm: searchTerm.value,
                              size: String(pageSize),
                              sort: sortOptions
                                  .map(({ direction, field }) => (direction === 'desc' ? `-${field}` : field))
                                  .join(','),
                          },
                      },
                  })
                : await validatingCobrainerApiFetcher.postJson(globalSearchJobResponseSchema, {
                      pathname,
                      payload: {
                          filter: filterPayload.unwrapOr(undefined),
                          paged: {
                              page: String(page),
                              size: String(pageSize),
                          },
                          sorted: {
                              fields: sortOptions,
                          },
                      },
                  });

            if (result.isOk) {
                return result.value;
            }
            throw result.error;
        },
        queryKey: [pathname, operation, filterPayload, page, pageSize, sortOptions, searchTerm],
    });
};
